import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { DateTimePicker } from '@material-ui/pickers';
import { DateTime } from 'luxon';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import InputAdornment from '@material-ui/core/InputAdornment';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Switch from '@material-ui/core/Switch';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import Fab from '@material-ui/core/Fab';

import RedButton from 'app/components/buttons/RedButton';

const renderDateInput = props => (
    <TextField
        variant="outlined"
        fullWidth
        margin="normal"
        onClick={props.onClick}
        id={props.id}
        value={props.value}
        disabled={props.disabled}
        label={props.label}
        helperText={props.helperText}
        {...props.inputProps}
    />
);

const useStyles = makeStyles(theme => ({
    section: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3)
    },
    formContainer: {
        overflowY: 'scroll',
        height: '100%',
        padding: theme.spacing(3)
    },
    fieldContainer: {
        overflowY: 'scroll',
        height: '100%',
        backgroundColor: theme.palette.grey[100]
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    height: {
        height: '100%'
    },
    fieldDeleteButton: {
        color: 'white',
        backgroundColor: theme.palette.error.main,
        '&:hover': {
            backgroundColor: theme.palette.error.dark
        }
    },
    field: {
        width: '100%',
        padding: theme.spacing(3)
    },
    selectedField: {
        backgroundColor: 'white'
    },
    buttonContainer: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: theme.spacing(3),
        paddingBottom: theme.spacing(12)
    },
    extendedIcon: {
        marginRight: theme.spacing(1),
      },
    formControl: {
        width: '100%'
    },
    list: {
        backgroundColor: theme.palette.grey[200],
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        overflowY: 'scroll'
    },
    listSection: {
        backgroundColor: 'inherit',
    },
    listItem: {
        backgroundColor: '#ffffff',
        marginBottom: theme.spacing(1),
    },
}));

const initTicket = {
    type: 'both',
    description: '',
    title: '',
    event_id: 0,
    price: 0,
    late_by: null,
    late_price: 0,
    amount: 0,
    pay_later: false,
    purchase_by: null,
    dates_allowed: 0,
};

const ticketGroups = [
    {
        title: "Everyone",
        id: 'both'
    },
    {
        title: "Event Group Only",
        id: 'group'
    },
    {
        title: "Guests Only",
        id: 'guest'
    },
    {
        title: "Admin Only",
        id: 'admin'
    }
];

const NewTicket = (props) => {

    const classes = useStyles();

    const { ticket, index, selected, onDelete, onSelect } = props;

    return (
        <div onClick={e => onSelect(index, e)}>
            <ListItem button selected={selected} className={classes.listItem}>
                <Grid container direction="column">
                    <Grid item>
                        <Typography variant="h6">
                            {ticket.title}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Typography variant="body1">
                            {ticket.description}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Grid container direction="row" justify="space-between">
                            <Grid item>
                                Price Per Ticket:  <strong>${ticket.price}</strong>
                            </Grid>
                            <Grid item>
                                Number of Tickets:  <strong>{ticket.amount}</strong>
                            </Grid>
                        </Grid>
                    </Grid>
                    {ticket.late_by &&
                        <Grid item style={{marginTop: 10}}>
                            <Grid container direction="row" justify="space-between">
                                <Grid item >
                                    Late Purchase Price:  <strong>${ticket.late_price}</strong>
                                </Grid>
                                <Grid item>
                                    Late Purchase Date:  <strong>{ticket.late_by.toLocaleString(DateTime.DATETIME_SMALL)}</strong>
                                </Grid>
                            </Grid>
                        </Grid>
                    }
                    {selected &&
                        <Grid item className={classes.marginTop}>
                            <Grid container direction="row" justify="flex-end">
                                <Grid item>
                                    <Tooltip title="Delete">
                                        <Fab size="small" className={classes.fieldDeleteButton} onClick={e => onDelete(index, e)}>
                                            <DeleteIcon />
                                        </Fab>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        </Grid>
                    }
                </Grid>
            </ListItem>
        </div>
    )
};


export default function EventTickets(props) {

    const classes = useStyles();

    const { event, tickets, setTickets, handleTickets, activeKey, setActiveKey} = props;

    const [ errors, setErrors ] = useState({});
    const [ latePrice, setLatePrice ] = useState(false);
    const [ payLater, setPayLater ] = useState(false);
    const [ cutoffDate, setCutoffDate ] = useState(false);

    // set latePrice to 'true' if already selected for that ticket

    useEffect(() => {
        if (tickets && tickets[activeKey] && tickets[activeKey].late_by !== null) {
            setLatePrice(true)
        } else setLatePrice(false)
    },  [activeKey]);

    // set late_by and late_price to null if latePrice turned to 'false'

    useEffect(() => {
        if (tickets[activeKey] && latePrice === false) {
            tickets[activeKey].late_by = null;
            tickets[activeKey].late_price = 0;
        }
    },  [latePrice]);

    // set purchase_by to 'true' if already selected for that ticket

    useEffect(() => {
        if (tickets && tickets[activeKey] && tickets[activeKey].purchase_by !== null) {
            setCutoffDate(true)
        } else setCutoffDate(false)
    },  [activeKey]);

    // set purchase_by to null if cuttoffDate turned to 'false'

    useEffect(() => {
        if (tickets && tickets[activeKey] && cutoffDate === false) {
            tickets[activeKey].purchase_by = null;
        }
    },  [cutoffDate]);

    // Create New Ticket

    const addTicket = (e) => {

        e.stopPropagation()
        const newTickets = [...tickets]

        newTickets.push(initTicket)
        setTickets(newTickets)
        setActiveKey(tickets.length)
        setLatePrice(false)
        setPayLater(false)
        setCutoffDate(false)
    }

    // Handlers

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    const handleLabelRequire = field => {
        if (field.required===true) {
            newLabel = [...field[label]]
        }
    }

    const handleSelect = (key, e) => {
        e.stopPropagation()
        setActiveKey(key);
    }

    const handleDelete = (key, e) => {
        e.stopPropagation()
        const newTickets = [...tickets];
        newTickets.splice(key,1);
        setTickets(newTickets);
        setActiveKey(newTickets.length - 1)
    }


    return (
        <Grid container direction="row" className={classes.height}>
            <Grid
                item
                xs={6}
                className={classes.fieldContainer}
                onClick={e => handleSelect(null, e)}
                >
                    <List className={classes.list}>
                        {tickets && tickets.map((ticket, index) => (
                                <NewTicket
                                    key={index}
                                    ticket={ticket}
                                    index={index}
                                    selected={activeKey===index}
                                    onDelete={handleDelete}
                                    onSelect={handleSelect}
                                />
                        ))}
                    </List>
                    <div className={classes.buttonContainer}>
                        <Tooltip title="Click to add a new ticket type.">
                            <Button
                                onClick={e => {addTicket(e);console.log(tickets)}}
                                variant="contained"
                                color="primary"
                                component="span"
                                startIcon={<AddIcon />}>
                                New Ticket
                            </Button>
                        </Tooltip>
                    </div>
                </Grid>
                <Grid item xs className={classes.formContainer}>
                    {(tickets.length==0) ? (
                        <Typography>
                            When you create a new ticket you will be able to select the details here.  Click "+ New Ticket" to get started!
                        </Typography>
                    ) : (activeKey===null) ? (
                        <Typography>
                            Click on any ticket to edit the details, or click "+ New Ticket" to add a new ticket.
                        </Typography>
                    ) : (
                        <div>
                            <Grid container spacing={2} direction="column">
                                <Grid item>
                                    <Typography variant='h6' >
                                        Edit Ticket
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <TextField
                                        id="ticket-title"
                                        label="Title (Type of Ticket)"
                                        value={tickets[activeKey].title}
                                        onChange={event => handleTickets('title', event)}
                                        variant="outlined"
                                        fullWidth
                                        placeholder="ie. General Admission"
                                        error={isError('title')}
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        id="ticket-description"
                                        value={tickets[activeKey].description}
                                        label='Ticket Description'
                                        onChange={event => handleTickets('description', event)}
                                        variant="outlined"
                                        fullWidth
                                        multiline
                                        rows={3}
                                        error={isError('description')}
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        id="select-group"
                                        select
                                        label="Select Ticket Group"
                                        value={tickets[activeKey].type}
                                        onChange={e => handleTickets('type', e)}
                                        variant="outlined"
                                        fullWidth
                                        error={isError('type')}
                                        helperText="Choose who this ticket is available to."
                                        >
                                        {ticketGroups.map((group) => (
                                            <MenuItem key={group.id} value={group.id}>
                                                {group.title}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item>
                                    <Grid
                                        container
                                        direction="row"
                                        spacing={2}
                                        justify="space-between"
                                        >
                                        <Grid item xs={6}>
                                            <TextField
                                                id="ticket-price"
                                                label="Price"
                                                variant="outlined"
                                                value={tickets[activeKey].price}
                                                onChange={event => handleTickets('price', event)}
                                                fullWidth
                                                type='number'
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                                    inputProps: {
                                                        min: 0,
                                                        step: 0.01
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                id="ticket-amount"
                                                value={tickets[activeKey].amount}
                                                label='Number of Tickets Available'
                                                onChange={event => handleTickets('amount', event)}
                                                variant="outlined"
                                                fullWidth
                                                type='number'
                                                error={isError('amount')}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={tickets[activeKey].pay_later}
                                                onChange={event => handleTickets('pay_later', event)}
                                                color="primary"
                                                value={payLater}
                                            />
                                        }
                                        label="Allow purchaser to pay later (ie. by check or cash)?"
                                        labelPlacement="end"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item >
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={latePrice}
                                                onChange={latePrice === true ? () => setLatePrice(false) : () => setLatePrice(true)}
                                                color="primary"
                                                value={latePrice}
                                            />
                                        }
                                        label="Charge a different price if purchased after a specific date?"
                                        labelPlacement="end"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item >
                                    <Typography variant='subtitle2' color="textSecondary" >
                                        Date of Price Increase
                                    </Typography>
                                    <DateTimePicker
                                        value={tickets[activeKey].late_by}
                                        inputVariant="outlined"
                                        onChange={event => handleTickets('late_by', event)}
                                        variant="outlined"
                                        TextFieldComponent={renderDateInput}
                                        minutesStep={15}
                                        disabled={!latePrice}
                                    />
                                </Grid>
                                <Grid item >
                                    <TextField
                                        id="ticket-late-price"
                                        label="Late Purchase Price"
                                        variant="outlined"
                                        value={tickets[activeKey].late_price}
                                        onChange={event => handleTickets('late_price', event)}
                                        fullWidth
                                        type='number'
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                            inputProps: {
                                                min: 0,
                                                step: 0.01
                                            }
                                        }}
                                        disabled={!latePrice}
                                    />
                                </Grid>
                                <Grid item >
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={cutoffDate}
                                                onChange={cutoffDate === true ? () => setCutoffDate(false) : () => setCutoffDate(true)}
                                                color="primary"
                                                value={cutoffDate}
                                            />
                                        }
                                        label="End ticket sales on a certain date?"
                                        labelPlacement="end"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item >
                                    <Typography variant='subtitle2' color="textSecondary" >
                                        Date to Stop Ticket Sales
                                    </Typography>
                                    <DateTimePicker
                                        value={tickets[activeKey].purchase_by}
                                        inputVariant="outlined"
                                        onChange={event => handleTickets('purchase_by', event)}
                                        variant="outlined"
                                        TextFieldComponent={renderDateInput}
                                        minutesStep={15}
                                        disabled={!cutoffDate}
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                spacing={3}
                                justify="flex-end"
                                className={classes.marginTop}
                                >
                                <Tooltip title="Delete">
                                    <RedButton
                                        size="small"
                                        onClick={e => handleDelete(activeKey, e)}
                                        >
                                        Delete Ticket
                                    </RedButton>
                                </Tooltip>
                            </Grid>
                        </div>
                    )}
            </Grid>
        </Grid>
    )
}
